import React from "react";
import {
    Card, Grid, CardContent, CardHeader,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup
} from "@mui/material";
import { Box } from "@mui/system";
import { AreaChart as AreaChartRe, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, Label } from 'recharts';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import PercentIcon from '@mui/icons-material/Percent';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import _ from "lodash";
import { transformChartDataToTable } from "../../../../helpers/UtilityHelper";
import { TableSkeleton } from "../../../../common/Skeletons";
import InfoBox from "../../../../common/InfoBox";
import useGetCourier from "../useGetCourier";
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import { ENTITY_COLORS } from "../../../charts/colors";

export default function CourierSplit({ data, courierIds }) {
    const { data: allCouriers, isLoading: isLoadingCouriers, isFetching: isFetchingCouriers } = useGetCourier()
    const [view, setView] = React.useState("chart")
    const [mode, setMode] = React.useState("count")

    if (isLoadingCouriers || isFetchingCouriers) return <TableSkeleton rows={10} />

    const allPresentCouriers = allCouriers.filter(row => courierIds.includes(row.id))
    const suffix = mode === 'count' ? 'value' : 'perc'

    const { rows: rowCount, allColumns } = transformChartDataToTable(data, [
        ...allPresentCouriers.map((z) => {
            const key = `c_${z.id}_value`
            return { key, label: z.id.replace(/_/g, " ") }
        })
    ])
    const { rows: rowPerc } = transformChartDataToTable(data, [
        ...allPresentCouriers.map((z) => {
            const key = `c_${z.id}_perc`
            return { key, label: z.id.replace(/_/g, " ") }
        })
    ])

    const infoBoxArray = []
    allPresentCouriers.forEach(item => {
        infoBoxArray.push({
            id: item.id,
            courier_name: item.name,
            total_count: _.sumBy(data, "c_" + item.id + '_value'),
        });
    });
    const sortedInfoBoxes = _.orderBy(infoBoxArray, "total_count", "desc")

    const chartData = data.map(row => {
        const total = sortedInfoBoxes.reduce((sum, s) => sum + (row[`c_${s.id}_${suffix}`] || 0), 0)
        return { ...row, _stackTotal: total }
    })

    return (
        <Card elevation={1}>
            <CardHeader
                title="Courier Wise Split"
                action={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ToggleButtonGroup color="primary" size="small" exclusive value={view} onChange={(_e, v) => { if (v) setView(v) }}>
                            <ToggleButton value="chart" title="View Chart">
                                <BarChartIcon />
                            </ToggleButton>
                            <ToggleButton value="table" title="View Table">
                                <TableChartIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <ToggleButtonGroup color="primary" size="small" exclusive value={mode} onChange={(_e, v) => { if (v) setMode(v) }}>
                            <ToggleButton value="count" title="View Count">
                                <LooksOneIcon />
                            </ToggleButton>
                            <ToggleButton value="percentage" title="View Percentage">
                                <PercentIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
            />
            <CardContent>
                <Grid container spacing={2}>
                    {sortedInfoBoxes.map((item, index) => (
                        <Grid item xs={2} key={index}>
                            <InfoBox header={item?.courier_name?.replace(/_/g, " ")} value={item.total_count} />
                        </Grid>
                    ))}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        {view === 'chart' && (
                            (!chartData || chartData.length === 0) ? <ChartEmpty height={380} /> : (
                            <ResponsiveContainer width="100%" height={380}>
                                <AreaChartRe data={chartData} margin={{ top: 30, right: 20, bottom: 10, left: 0 }}>
                                    <CartesianGrid strokeDasharray="5 5" stroke="#dee2e6" />
                                    <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                                    <YAxis fontSize="11px" fontWeight="500">
                                        <Label value="# Shipments Allocated" angle={-90} position="insideLeft" style={{ fontSize: 11, fontWeight: 500, fill: '#666', textAnchor: 'middle' }} />
                                    </YAxis>
                                    <Tooltip content={<ChartTooltip />} />
                                    <Legend verticalAlign="top" align="left" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                                    {sortedInfoBoxes.map((c, i) => {
                                        const color = ENTITY_COLORS[i % ENTITY_COLORS.length]
                                        const isLast = i === sortedInfoBoxes.length - 1
                                        return (
                                            <Area
                                                key={c.id}
                                                type="monotone"
                                                dataKey={`c_${c.id}_${suffix}`}
                                                name={c.courier_name?.replace(/_/g, " ")}
                                                stackId="courier"
                                                stroke={color}
                                                fill={color}
                                                fillOpacity={0.65}
                                                unit={mode === 'percentage' ? '%' : ''}
                                                animationEasing="ease-in-out"
                                                animationDuration={1000}
                                            >
                                                {isLast && (
                                                    <LabelList
                                                        dataKey="_stackTotal"
                                                        position="top"
                                                        style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }}
                                                        formatter={(v) => (v == null || v === 0) ? '' : `${v}${mode === 'percentage' ? '%' : ''}`}
                                                    />
                                                )}
                                            </Area>
                                        )
                                    })}
                                </AreaChartRe>
                            </ResponsiveContainer>
                            )
                        )}
                        {view === 'table' && (
                            <TableContainer sx={{ maxHeight: 1200 }}>
                                <Table size="small" density sx={{ borderCollapse: 'collapse' }}>
                                    <TableHead sx={{ backgroundColor: '#000', color: '#fff' }}>
                                        <TableRow sx={{ borderBottom: '1px solid #ccc' }}>
                                            <TableCell sx={{ borderRight: '1px solid #ccc', color: '#fff', position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#000' }} colSpan={1}>Name</TableCell>
                                            {allColumns.map((val, j) => (
                                                <TableCell key={j} sx={{ borderRight: '1px solid #ccc', color: '#fff' }} colSpan={2}>{val}</TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#000' }} />
                                            {allColumns.map((_val, i) => (
                                                <React.Fragment key={i}>
                                                    <TableCell sx={{ borderRight: '1px solid #ccc', color: '#fff' }}>Count</TableCell>
                                                    <TableCell sx={{ borderRight: '1px solid #ccc', color: '#fff' }}>%</TableCell>
                                                </React.Fragment>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rowCount.map((row, i) => {
                                            const inTatData = _.find(rowPerc, { name: row.name })
                                            return (
                                                <TableRow key={i} sx={{ borderBottom: '1px solid #ccc' }}>
                                                    <TableCell sx={{ borderRight: '1px solid #ccc', position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#000', color: '#fff' }}>{_.filter(allCouriers, { id: row.name })[0]?.parent + " / " + _.filter(allCouriers, { id: row.name })[0]?.id}</TableCell>
                                                    {allColumns.map((val, j) => (
                                                        <React.Fragment key={j}>
                                                            <TableCell sx={{ borderRight: '1px solid #ccc' }}>{_.get(row, val, 0)}</TableCell>
                                                            <TableCell sx={{ borderRight: '1px solid #ccc' }}>{_.get(inTatData, val, 0) + '%'}</TableCell>
                                                        </React.Fragment>
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
